import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SuperAdminSidebar from '../../../components/SuperAdminsidebar';
import Pagination from '../../../../components/Pagination';
import { fetchExpenses } from '../../../../services/superadmin_services/expense_services';
import { searchUsers } from '../../../../services/superadmin_services/usersearch_service';

const ManageExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 0,
    count: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    owner: '',
    created_by: '',
    date_from: '',
    date_to: '',
    sort: 'desc'
  });

  // User search states for dropdowns
  const [ownerUsers, setOwnerUsers] = useState([]);
  const [createdByUsers, setCreatedByUsers] = useState([]);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const [createdBySearchTerm, setCreatedBySearchTerm] = useState('');
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [showCreatedByDropdown, setShowCreatedByDropdown] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState(null);

  // Load expenses
  const loadExpenses = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetchExpenses({
        ...filters,
        page,
        page_size: pagination.page_size
      });

      setExpenses(response.data.results);
      setPagination(prev => ({
        ...prev,
        page: response.data.page,
        total_pages: response.data.total_pages,
        count: response.data.count
      }));
    } catch (err) {
      setError(err.message);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  // Search users for owner dropdown
  const searchOwnerUsers = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setOwnerUsers([]);
      setShowOwnerDropdown(false);
      return;
    }
    
    try {
      const response = await searchUsers(searchTerm, 10);
      setOwnerUsers(response.data.results || []);
      setShowOwnerDropdown(true);
    } catch (err) {
      console.error('Error searching owner users:', err);
      setOwnerUsers([]);
      setShowOwnerDropdown(false);
    }
  };

  // Search users for created_by dropdown
  const searchCreatedByUsers = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setCreatedByUsers([]);
      setShowCreatedByDropdown(false);
      return;
    }
    
    try {
      const response = await searchUsers(searchTerm, 10);
      setCreatedByUsers(response.data.results || []);
      setShowCreatedByDropdown(true);
    } catch (err) {
      console.error('Error searching created_by users:', err);
      setCreatedByUsers([]);
      setShowCreatedByDropdown(false);
    }
  };

  // Handle owner selection
  const handleOwnerSelect = (user) => {
    setSelectedOwner(user);
    setOwnerSearchTerm(`${user.first_name} ${user.last_name} (${user.username})`);
    handleFilterChange('owner', user.id);
    setShowOwnerDropdown(false);
    setOwnerUsers([]);
  };

  // Handle created by selection
  const handleCreatedBySelect = (user) => {
    setSelectedCreatedBy(user);
    setCreatedBySearchTerm(`${user.first_name} ${user.last_name} (${user.username})`);
    handleFilterChange('created_by', user.id);
    setShowCreatedByDropdown(false);
    setCreatedByUsers([]);
  };

  // Clear owner selection
  const clearOwnerSelection = () => {
    setSelectedOwner(null);
    setOwnerSearchTerm('');
    handleFilterChange('owner', '');
    setShowOwnerDropdown(false);
    setOwnerUsers([]);
  };

  // Clear created by selection
  const clearCreatedBySelection = () => {
    setSelectedCreatedBy(null);
    setCreatedBySearchTerm('');
    handleFilterChange('created_by', '');
    setShowCreatedByDropdown(false);
    setCreatedByUsers([]);
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle filter submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    loadExpenses(1);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (page_size) => {
    setPagination(prev => ({ ...prev, page_size, page: 1 }));
  };

  // Load expenses when pagination changes
  useEffect(() => {
    loadExpenses(pagination.page);
  }, [pagination.page, pagination.page_size]);

  // Format currency in XAF (Cameroon Francs)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  useEffect(() => {
    loadExpenses();
  }, []);

  useEffect(() => {
    if (ownerSearchTerm && !selectedOwner) {
      const timeoutId = setTimeout(() => searchOwnerUsers(ownerSearchTerm), 300);
      return () => clearTimeout(timeoutId);
    } else if (!ownerSearchTerm) {
      setOwnerUsers([]);
      setShowOwnerDropdown(false);
    }
  }, [ownerSearchTerm, selectedOwner]);

  useEffect(() => {
    if (createdBySearchTerm && !selectedCreatedBy) {
      const timeoutId = setTimeout(() => searchCreatedByUsers(createdBySearchTerm), 300);
      return () => clearTimeout(timeoutId);
    } else if (!createdBySearchTerm) {
      setCreatedByUsers([]);
      setShowCreatedByDropdown(false);
    }
  }, [createdBySearchTerm, selectedCreatedBy]);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <SuperAdminSidebar />
      
      <div className="flex-1 ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Manage Expenses</h1>
            <p className="mt-2 text-gray-600">View and manage all user expenses</p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <form onSubmit={handleFilterSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Title, payment method, vendor, receipt number..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                  />
                </div>

                {/* Owner Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Owner
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search owner..."
                      value={ownerSearchTerm}
                      onChange={(e) => {
                        setOwnerSearchTerm(e.target.value);
                        if (selectedOwner) {
                          setSelectedOwner(null);
                          handleFilterChange('owner', '');
                        }
                      }}
                      onFocus={() => {
                        if (ownerUsers.length > 0) {
                          setShowOwnerDropdown(true);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                    />
                    {selectedOwner && (
                      <button
                        type="button"
                        onClick={clearOwnerSelection}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    )}
                    {showOwnerDropdown && ownerUsers.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                        {ownerUsers.map(user => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => handleOwnerSelect(user)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium">{user.first_name} {user.last_name}</div>
                            <div className="text-sm text-gray-500">@{user.username} • {user.email}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Created By Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created By
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search creator..."
                      value={createdBySearchTerm}
                      onChange={(e) => {
                        setCreatedBySearchTerm(e.target.value);
                        if (selectedCreatedBy) {
                          setSelectedCreatedBy(null);
                          handleFilterChange('created_by', '');
                        }
                      }}
                      onFocus={() => {
                        if (createdByUsers.length > 0) {
                          setShowCreatedByDropdown(true);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                    />
                    {selectedCreatedBy && (
                      <button
                        type="button"
                        onClick={clearCreatedBySelection}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    )}
                    {showCreatedByDropdown && createdByUsers.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                        {createdByUsers.map(user => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => handleCreatedBySelect(user)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium">{user.first_name} {user.last_name}</div>
                            <div className="text-sm text-gray-500">@{user.username} • {user.email}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Date From */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date From
                  </label>
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date To
                  </label>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                  />
                </div>

                {/* Sort */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort by Date
                  </label>
                  <select
                    value={filters.sort}
                    onChange={(e) => handleFilterChange('sort', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-secondary-500"
                >
                  Apply Filters
                </button>
              </div>
            </form>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
              {error}
            </div>
          )}

          {/* Expenses Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Expenses ({pagination.count} total)
              </h2>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
                <p className="mt-2 text-gray-600">Loading expenses...</p>
              </div>
            ) : expenses.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No expenses found matching your criteria.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Owner
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {expenses.map((expense) => (
                      <tr key={expense.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {expense.title}
                          </div>
                          {expense.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {expense.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(expense.amount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {expense.vendor || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {expense.owner_details?.first_name} {expense.owner_details?.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            @{expense.owner_details?.username}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(expense.date_of_expense)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Link
                            to={`/superadmin/expenses/${expense.id}`}
                            className="text-secondary-600 hover:text-secondary-900"
                          >
                            View Details
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.total_pages}
              totalItems={pagination.count}
              pageSize={pagination.page_size}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              loading={loading}
              itemName="expenses"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageExpensesPage;